/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import {
  Button,
  ButtonGroup,
  Dialog,
  IconButton,
  InlineNotification,
  SelectField,
  SelectOption,
  Table,
  type TableColumn,
  Tag,
  TextField,
  Typography2,
} from '@coursera/cds-core';
import { AddIcon, TrashIcon } from '@coursera/cds-icons';

import { CopyProgramLink } from 'bundles/enterprise-admin-program-creation/components/CopyProgramLink';
import type { EnterpriseUserProgramAllowListElement } from 'bundles/enterprise-admin-program-users/utils/EnterpriseUserProgramAllowListQuery';
import { copyText } from 'bundles/enterprise-ui/utils';

import _t from 'i18n!nls/enterprise-admin-learners';

import { useDomainAllowlist } from '../../hooks/useDomainAllowlist';

const { HeadingGroup, Content, Actions } = Dialog;

const styles = {
  subtitle: css`
    margin-bottom: var(--cds-spacing-200);
  `,
  warning: css`
    margin-bottom: var(--cds-spacing-300);
  `,
  fieldsHeading: css`
    margin-bottom: var(--cds-spacing-100);
  `,
  addRow: css`
    display: flex;
    align-items: flex-end;
    gap: var(--cds-spacing-200);
    margin-bottom: var(--cds-spacing-200);
  `,
  domainField: css`
    flex: 1;
  `,
  groupField: css`
    width: 280px;
    flex-shrink: 0;
  `,
  inlineNotification: css`
    margin-bottom: var(--cds-spacing-200);
  `,
  successInfoNotification: css`
    margin: var(--cds-spacing-200) 0;
  `,
  activeDomainsHeading: css`
    margin-top: var(--cds-spacing-400);
    margin-bottom: var(--cds-spacing-100);
  `,
  linkField: css`
    margin-bottom: var(--cds-spacing-300);
  `,
  tagRow: css`
    display: flex;
    flex-wrap: wrap;
    gap: var(--cds-spacing-100);
    padding-bottom: var(--cds-spacing-300);
  `,
  // Tag's `size` prop only offers 'small' | 'medium' (already the default) — no larger built-in
  // option, and its internal `.medium` padding rule outweighs a plain override, hence !important.
  domainTag: css`
    padding: var(--cds-spacing-150) var(--cds-spacing-200) !important;
    /* stylelint-disable-next-line cds-stylelint/cds-typography-utility */
    font: var(--cds-typography-body-primary) !important;
  `,
};

export type DomainAllowlistModalProps = {
  open: boolean;
  onClose: () => void;
  onBack: () => void;
  /** Set when the group step should be skipped — pre-selects and locks the group picker. */
  autoSelectedGroupId?: string;
};

/** Fixed rows-per-page for the Active domains table — the page-size selector offers no other
 * choice (a single-option `pageSizeInputOptions`), so it's effectively disabled. */
const DOMAINS_PAGE_SIZE = 5;

type Step = 'input' | 'success';

const getAllowlistColumns = (
  groupLabel: string,
  onRequestRemove: (domain: string) => void
): TableColumn<EnterpriseUserProgramAllowListElement, keyof EnterpriseUserProgramAllowListElement>[] => [
  {
    accessor: 'emailDomain',
    label: _t('Active domain'),
  },
  {
    accessor: 'programId',
    label: _t('Groups'),
    renderCell: () => <Typography2 component="span">{groupLabel}</Typography2>,
  },
  {
    accessor: 'rosterId',
    'aria-label': _t('Row actions'),
    width: '80px',
    justify: 'end',
    renderCell: (row) => (
      <IconButton
        onClick={() => onRequestRemove(row.emailDomain)}
        icon={<TrashIcon size="medium" />}
        variant="ghost"
        aria-label={_t('Remove #{domain}', { domain: row.emailDomain })}
        tooltip={_t('Remove domain')}
      />
    ),
  },
];

const RemoveDomainConfirmDialog = ({
  domain,
  onCancel,
  onConfirm,
  isRemoving,
}: {
  domain: string | undefined;
  onCancel: () => void;
  onConfirm: () => void;
  isRemoving: boolean;
}) => (
  <Dialog open={domain !== undefined} onClose={onCancel} width="medium">
    <HeadingGroup>{_t('Remove domain')}</HeadingGroup>
    <Content>
      <Typography2 component="p" variant="bodyPrimary">
        {_t('Learners with a #{domain} email will no longer get automatic access.', { domain: domain ?? '' })}
      </Typography2>
    </Content>
    <Actions>
      <ButtonGroup
        primaryButtonProps={{ children: _t('Remove'), onClick: onConfirm, loading: isRemoving }}
        alternateButtonProps={{ children: _t('Cancel'), variant: 'secondary', onClick: onCancel, disabled: isRemoving }}
      />
    </Actions>
  </Dialog>
);

/**
 * Group-scoped add/remove-domain UI (ENTADMIN-1956), replacing the ENTADMIN-1951 scaffolding.
 * Renders as two full dialogs from one component (mirrors `AddDomainSteps`): the add/remove
 * screen, then a "setup is live" shareable-link screen once the admin saves.
 */
const DomainAllowlistModal = ({ open, onClose, onBack, autoSelectedGroupId }: DomainAllowlistModalProps) => {
  const [step, setStep] = React.useState<Step>('input');
  const domainAllowlist = useDomainAllowlist({ open, autoSelectedGroupId });

  React.useEffect(() => {
    if (open) setStep('input');
  }, [open]);

  if (step === 'success') {
    return (
      <Dialog open={open} onClose={onClose} width="large">
        <HeadingGroup supportText={_t('Share the link below with your team.')}>
          {_t('Your domain setup is live')}
        </HeadingGroup>
        <Content>
          <InlineNotification severity="information" css={styles.successInfoNotification}>
            {_t(
              'Anyone with a matching domain who clicks this link will get access and consume a license. You can manage or remove domains anytime from Settings.'
            )}
          </InlineNotification>
          {domainAllowlist.targetProgramSlug && (
            <div css={styles.linkField}>
              <CopyProgramLink
                programSlug={domainAllowlist.targetProgramSlug}
                onCopy={copyText}
                inputLabel={_t('Shareable link')}
              />
            </div>
          )}
          <Typography2 variant="subtitleMedium" component="h3" css={styles.fieldsHeading}>
            {_t('Active domains')}
          </Typography2>
          <div css={styles.tagRow}>
            {domainAllowlist.allowlist.map((entry) => (
              <Tag key={entry.emailDomain} priority="secondary" css={styles.domainTag}>
                {entry.emailDomain}
              </Tag>
            ))}
          </div>
        </Content>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} width="large">
      <HeadingGroup
        supportText={_t(
          'Anyone who signs in with a matching email domain will automatically get access, no invite needed. You can add multiple domains.'
        )}
      >
        {_t('Add your domain name(s)')}
      </HeadingGroup>
      <Content>
        <InlineNotification severity="warning" css={styles.warning}>
          {_t(
            'Subdomains are included automatically. Avoid free email domains like gmail.com or yahoo.com, anyone with that domain will consume a license.'
          )}
        </InlineNotification>
        <Typography2 variant="subtitleMedium" component="h3" css={styles.fieldsHeading}>
          {_t('Domain name(s)')}
        </Typography2>
        <Typography2 component="p" color="supportText" css={styles.subtitle}>
          {_t('Added domains appear below. Subdomains are included automatically.')}
        </Typography2>
        <div css={styles.addRow}>
          <TextField
            css={styles.domainField}
            aria-label={_t('Domain name(s)')}
            placeholder={_t('domain.com, example.org')}
            value={domainAllowlist.domainInputText}
            onChange={(event) => domainAllowlist.onDomainInputTextChange(event.target.value)}
            validationStatus={domainAllowlist.domainInputError ? 'error' : undefined}
            validationLabel={domainAllowlist.domainInputError}
            fullWidth
          />
          {domainAllowlist.showGroupPicker && (
            <SelectField
              css={styles.groupField}
              aria-label={_t('Choose a group')}
              optional
              disabled={domainAllowlist.isGroupSelectionLocked}
              value={domainAllowlist.selectedGroupId}
              onChange={(event) => domainAllowlist.onSelectGroup(String(event.target.value))}
            >
              {[
                <SelectOption key="no-group" value="">
                  {_t('No group – Add to All learners')}
                </SelectOption>,
                ...domainAllowlist.groupOptions.map((group) => (
                  <SelectOption key={group.id} value={group.id}>
                    {group.name}
                  </SelectOption>
                )),
              ]}
            </SelectField>
          )}
          <Button
            variant="secondary"
            icon={<AddIcon />}
            iconPosition="before"
            onClick={domainAllowlist.addDomains}
            loading={domainAllowlist.isAddingDomains}
            disabled={
              !domainAllowlist.domainInputText.trim() || domainAllowlist.hasNoRoster || domainAllowlist.hasRosterError
            }
          >
            {_t('Add')}
          </Button>
        </div>
        <div role="alert">
          {domainAllowlist.hasNoRoster && (
            <InlineNotification severity="error" css={styles.inlineNotification}>
              {_t('This group has no roster set up yet. Contact support to add domains for it.')}
            </InlineNotification>
          )}
        </div>
        <div role="alert">
          {domainAllowlist.hasRosterError && (
            <InlineNotification severity="error" css={styles.inlineNotification}>
              {_t('Something went wrong while loading this group. Please try again.')}
            </InlineNotification>
          )}
        </div>
        <div role="alert">
          {domainAllowlist.mutationError && (
            <InlineNotification severity="error" css={styles.inlineNotification}>
              {domainAllowlist.mutationError}
            </InlineNotification>
          )}
        </div>
        <Typography2 variant="subtitleMedium" component="h3" css={styles.activeDomainsHeading}>
          {_t('Active domains')}
        </Typography2>
        {!domainAllowlist.loading && domainAllowlist.allowlist.length === 0 ? (
          <Typography2 component="p" color="supportText">
            {_t('No active domain yet')}
          </Typography2>
        ) : (
          <Table<EnterpriseUserProgramAllowListElement, keyof EnterpriseUserProgramAllowListElement>
            aria-label={_t('Active domains')}
            columns={getAllowlistColumns(domainAllowlist.targetGroupLabel, domainAllowlist.requestRemoveDomain)}
            rows={domainAllowlist.allowlist}
            totalRows={domainAllowlist.allowlist.length}
            status={domainAllowlist.loading ? 'loading' : 'idle'}
            defaultPageSize={DOMAINS_PAGE_SIZE}
            pageSizeInputOptions={[{ label: String(DOMAINS_PAGE_SIZE), value: DOMAINS_PAGE_SIZE }]}
          />
        )}
      </Content>
      <Actions>
        <ButtonGroup
          primaryButtonProps={{
            children: _t('Save'),
            onClick: () => setStep('success'),
            disabled: !domainAllowlist.canSave,
          }}
          alternateButtonProps={{ children: _t('Back'), variant: 'secondary', onClick: onBack }}
        />
      </Actions>
      <RemoveDomainConfirmDialog
        domain={domainAllowlist.pendingDeleteDomain}
        onCancel={domainAllowlist.cancelRemoveDomain}
        onConfirm={domainAllowlist.confirmRemoveDomain}
        isRemoving={domainAllowlist.isRemovingDomain}
      />
    </Dialog>
  );
};

export default DomainAllowlistModal;
